import React, { useRef, useEffect, useMemo, useLayoutEffect, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import useSimulationStore from '../../store/simulationStore';
import { ElementProperties } from '../../types/simulation';
import { shallow } from 'zustand/shallow';
import { threeSceneManager } from '../../utils/ThreeSceneManager';

// Define the expected structure for a single atom within the geometry
interface AtomGeometry {
    element: string;
    position?: [number, number, number];
}

// Define the expected structure for the processed species geometry prop
interface ProcessedSpeciesGeometry {
    atoms: AtomGeometry[];
    // Potentially add bonds or other properties later if needed
}

// Helper function to get element properties safely
// Now includes handling for atomicNumber
const getElementProps = (elementSymbol: string, elementProperties: Record<string, ElementProperties>): ElementProperties => {
    const props = elementProperties[elementSymbol.toUpperCase()];
    if (props) {
        // Return a new object to ensure all fields are present and provide default atomicNumber if missing
        return {
            radius: props.radius ?? 0.5, // Use nullish coalescing for safety
            color: props.color || '#FFFFFF',
            mass: props.mass || 1,
            atomicNumber: props.atomicNumber ?? 0 // Default atomicNumber to 0 if undefined/null
        };
    }

    const defaultPropsFromMap = elementProperties.DEFAULT;
    if (defaultPropsFromMap) {
        return {
            radius: defaultPropsFromMap.radius ?? 0.5,
            color: defaultPropsFromMap.color || '#FFFFFF',
            mass: defaultPropsFromMap.mass || 1,
            atomicNumber: defaultPropsFromMap.atomicNumber ?? 0
        };
    }
    // Absolute fallback
    return { color: '#ffffff', radius: 0.5, mass: 1, atomicNumber: 0 };
};

interface InstancedSpeciesMeshProps {
    speciesName: string;
    speciesGeometry: ProcessedSpeciesGeometry; // Use the more specific type
    elementProperties: Record<string, ElementProperties>; // Global element properties map
    // NOTE: No particles prop - data comes directly from ThreeSceneManager
    metalness: number;
    roughness: number;
    emissiveIntensity: number;
    particleScale: number;
}

// --- Custom Shader Definitions ---
const swirlVertexShader = `
varying vec2 vUv;
varying vec3 vWorldPosition;
varying vec3 vWorldNormal;
attribute vec3 instanceColor;
varying vec3 vInstanceColor;

void main() {
  vUv = uv;
  vInstanceColor = instanceColor;

  vec4 worldPos_cs = modelMatrix * instanceMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos_cs.xyz;

  vWorldNormal = normalize((modelMatrix * instanceMatrix * vec4(normal, 0.0)).xyz);

  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}
`;
const swirlFragmentShader = `
precision highp float;
varying vec2 vUv;
varying vec3 vWorldPosition;
varying vec3 vWorldNormal;
varying vec3 vInstanceColor;
uniform float time;
uniform vec3 u_cameraPosition;
uniform float u_particleScale;

const float PI = 3.14159265;

vec3 cmap1_st(float x) { return pow(.5+.5*cos(PI * x + vec3(1.0,2.0,3.0)), vec3(2.5)); }
vec3 cmap2_st(float x) {
    vec3 col = vec3(0.35, 1.0, 1.0)*(cos(PI * x * vec3(1.0) + 0.75 * vec3(2.0,1.0,3.0))*.5+.5);
    col *= col * col;
    return col;
}
vec3 cmap3_st(float x) {
    vec3 color1 = vec3(1.0, 0.0, 0.0); // Red
    vec3 color2 = vec3(0.0, 1.0, 0.0); // Green
    vec3 col = mix(color2, color1, cos(x/1.25)*.5+.5);
    col*=col*col;
    return col;
}
vec3 cmap_adapted(float x, float currentTime) {
    float t_cycle = mod(currentTime, 300.0);
    if (t_cycle < 100.0) return cmap1_st(x);
    else if (t_cycle < 200.0) return cmap2_st(x);
    else return cmap3_st(x);
}

// Inspired by the "Hollow Cone Turbulence" Shadertoy - MODIFIED FOR SWIRL
float calculateHollowConePattern(vec3 query_pos_ws, float currentTime) {
    vec3 p = query_pos_ws * 3.0; // Initial scaling of world position - adjust for pattern size

    float t = currentTime * 0.2; // Global time for animation, significantly slowed down from iTime

    // Initial p transformations (keep twist/expansion for now)
    p.z += cos(t);

    float twist_angle_comp = p.y * 0.5 + t * 0.5;
    mat2 twist_rot = mat2(cos(twist_angle_comp), -sin(twist_angle_comp), 
                          sin(twist_angle_comp),  cos(twist_angle_comp));
    p.xz = twist_rot * p.xz;
    
    float expansion_factor = max(p.y * 0.1 + 1.0, 0.1);
    p.xz /= expansion_factor;

    // Turbulence loop - MODIFIED TO ADD SWIRL ROTATION PER OCTAVE
    float d_turb = 2.0;
    for (int j = 0; j < 6; j++) {
        if (d_turb >= 15.0) break;

        // --- Add a rotation to 'p' within the loop ---
        float swirl_angle = (p.z * 0.1 + t * 0.5) * (1.0 + float(j) * 0.2);
        float c_swirl = cos(swirl_angle);
        float s_swirl = sin(swirl_angle);
        mat2 swirl_rot = mat2(c_swirl, -s_swirl, s_swirl, c_swirl);
        vec3 p_swirled = p;
        p_swirled.xz = swirl_rot * p_swirled.xz;
        // ------------------------------------------------

        // Calculate turbulence term using the swirled position
        float t_scaled_for_offset = t * 0.1;
        vec3 offset = vec3(t_scaled_for_offset, t, d_turb);
        vec3 cos_term = cos((p_swirled.yzx - offset) * d_turb);

        // Apply turbulence feedback to the original p
        p += cos_term / d_turb;

        d_turb /= 0.6;
    }

    // Base shape calculation (Hollow Cone DE) - keep for now
    float de_core = abs(length(p.xz) + p.y * 0.3 - 0.5);
    float pattern_from_de = 1.0 - clamp(de_core / 3.5, 0.0, 1.0);
    
    return pattern_from_de * pattern_from_de;
}

float calculate3DPattern(vec3 initialPos, float currentTime, float particleScale) {
    float safeParticleScale = max(particleScale, 0.01);
    float baseFrequency = 6.0;
    float dynamicFrequency = baseFrequency / safeParticleScale;
    vec3 p = initialPos * dynamicFrequency;

    float pattern = 0.0;
    float amplitude = 0.5;
    float c = cos(currentTime * 0.0005);
    float s = sin(currentTime * 0.0005);
    mat2 rot = mat2(c, -s, s, c);
    p.xy = rot * p.xy;
    p.xz = rot * p.xz;

    for (int i = 0; i < 5; i++) {
        float angle = currentTime * 0.00375 * (1.0 + float(i) * 0.5);
        float c_oct = cos(angle);
        float s_oct = sin(angle);
        if (mod(float(i), 3.0) == 0.0) { p.xy = mat2(c_oct, -s_oct, s_oct, c_oct) * p.xy; }
        else if (mod(float(i), 3.0) == 1.0) { p.yz = mat2(c_oct, -s_oct, s_oct, c_oct) * p.yz; }
        else { p.zx = mat2(c_oct, -s_oct, s_oct, c_oct) * p.zx; }
        pattern += amplitude * (0.5 + 0.5 * sin(p.x + cos(p.y) + sin(p.z)));
        p = p * 2.0 + 0.5 * cos(currentTime * 0.00025 + p.yzx * 0.5);
        amplitude *= 0.5;
    }
    return clamp(pattern, 0.0, 1.0);
}

float fresnelSchlick(float cosTheta, float F0) {
    return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
}

void main() {
    vec3 N = normalize(vWorldNormal);
    vec3 V = normalize(u_cameraPosition - vWorldPosition);

    float depth_offset = 0.0025;
    vec3 query_pos = vWorldPosition - N * depth_offset;

    float patternVal = calculate3DPattern(query_pos, time, u_particleScale);

    // --- Define the two states ---
    
    // State 1: Base Atom (when patternVal is low)
    vec3 baseColor = vInstanceColor;
    float baseAlpha = 1.0; // Fully opaque

    // State 2: Pattern Effect (when patternVal is high)
    vec3 patternColor = cmap_adapted(patternVal, time * 0.2); // Get color from cmap
    patternColor = pow(patternColor, vec3(1.0/2.2));
    patternColor = clamp(patternColor, 0.0, 1.0);
    float patternAlpha = 0.35; // Make this state very transparent (adjust 0.05 if needed)

    // --- Define the transition ---
    
    float transitionStart = 0.4; // Below this, mostly base state
    float transitionEnd = 0.6;   // Above this, mostly pattern state

    // Calculate the mix factor using smoothstep
    float mixFactor = smoothstep(transitionStart, transitionEnd, patternVal);

    // --- Mix the states ---
    
    vec3 final_color = mix(baseColor, patternColor, mixFactor);
    float final_alpha = mix(baseAlpha, patternAlpha, mixFactor);

    gl_FragColor = vec4(final_color, clamp(final_alpha, 0.0, 1.0)); // Ensure alpha is valid

    // --- Previous logic commented for reference (might need parts if reverting) ---
    /*
    // Calculate modulation color/intensity (Old approach)
    vec3 modulationColor = cmap_adapted(patternVal, time * 0.2);
    modulationColor = pow(modulationColor, vec3(1.0/2.2));
    modulationColor = clamp(modulationColor, 0.0, 1.0);
    float modulationIntensity = dot(modulationColor, vec3(0.299, 0.587, 0.114));
    float boostedIntensity = 0.6 + modulationIntensity * 0.7;
    vec3 emissiveColor = modulationColor * patternVal * 0.5;
    vec3 tintedBaseColor = vInstanceColor * boostedIntensity;
    vec3 final_color_old = tintedBaseColor + emissiveColor;
    final_color_old = clamp(final_color_old, 0.0, 1.0);
    // Alpha (using user's values)
    float min_alpha = 0.7;
    float final_alpha_old = min_alpha + patternVal * (1.0 - min_alpha);
    gl_FragColor = vec4(final_color_old, clamp(final_alpha_old, 0.5, 1.0));
    */
}
`;


const testBlueVertexShader = `
void main() {
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}
`;
const testBlueFragmentShader = `
precision mediump float;
void main() {
    gl_FragColor = vec4(0.1, 0.2, 0.8, 1.0); // Blue
}
`;

// --- Custom Shader Map ---
const customShaderRegistry = {
    SwirlEffect: {
        vertex: swirlVertexShader,
        fragment: swirlFragmentShader,
        uniforms: {
            time: { value: 0.0 },
            u_cameraPosition: { value: new THREE.Vector3() },
            u_particleScale: { value: 1.0 }
        }
    },
    TestBlue: {
        vertex: testBlueVertexShader,
        fragment: testBlueFragmentShader,
        uniforms: {}
    }
};

const InstancedSpeciesMesh: React.FC<InstancedSpeciesMeshProps> = ({
    speciesName,
    speciesGeometry,
    elementProperties,
    metalness,
    roughness,
    emissiveIntensity,
    particleScale,
}) => {
    const activeVisShader = useSimulationStore((state) => state.activeVisShader);
    // Element-level coloring system
    const elementColors = useSimulationStore((state) => state.elementColors);
    const elementFocus = useSimulationStore((state) => state.elementFocus);
    const reactionFocus = useSimulationStore((state) => state.reactionFocus);
    const reactionColors = useSimulationStore((state) => state.reactionColors);
    const speciesFocus = useSimulationStore((state) => state.speciesFocus);
    const speciesColors = useSimulationStore((state) => state.speciesColors);
    const { clock, camera } = useThree();

    // Note: speciesVisual system removed - using element-level coloring instead

    // Dynamic max capacity - calculated once on mount, then grows only when needed
    // This prevents constant reallocation while adapting to different reaction scales
    const [maxInstances, setMaxInstances] = useState(200); // Initial reasonable default

    // Track peak particle count and grow buffer if needed (but never shrink)
    const peakCountRef = useRef(0);

    const atomsByElement = useMemo(() => {
        const grouped: { [elementSymbol: string]: THREE.Vector3[] } = {};
        if (!speciesGeometry || !speciesGeometry.atoms) {
            return grouped;
        }
        speciesGeometry.atoms.forEach((atom: AtomGeometry) => {
            const symbol = atom.element.toUpperCase();
            if (!grouped[symbol]) {
                grouped[symbol] = [];
            }
            grouped[symbol].push(new THREE.Vector3(...(atom.position || [0, 0, 0])));
        });
        return grouped;
    }, [speciesGeometry, speciesName]);

    const elementTypes = Object.keys(atomsByElement);
    const meshRefs = useRef<{ [elementSymbol: string]: THREE.InstancedMesh }>({});

    const geometriesByElement = useMemo(() => {
        const geomMap: { [key: string]: THREE.SphereGeometry } = {};
        elementTypes.forEach(symbol => {
            const geom = new THREE.SphereGeometry(1, 32, 16);
            // Don't set instanceColor here - it will be set in the useLayoutEffect
            // with proper sizing based on maxInstances
            geomMap[symbol] = geom;
        });
        return geomMap;
    }, [elementTypes]);

    // Dispose geometries when they change or component unmounts
    useEffect(() => {
        return () => {
            Object.values(geometriesByElement).forEach(geom => {
                // Dispose all buffer attributes
                Object.keys(geom.attributes).forEach(attributeName => {
                    const attribute = geom.attributes[attributeName];
                    if (attribute && 'array' in attribute) {
                        (attribute as any).array = null;
                    }
                });
                geom.dispose();
            });
        };
    }, [geometriesByElement]);

    // Dispose mesh materials and geometries on unmount
    useEffect(() => {
        return () => {
            Object.values(meshRefs.current).forEach(mesh => {
                if (mesh) {
                    // Dispose instanceMatrix (critical for memory leak prevention)
                    if (mesh.instanceMatrix) {
                        (mesh.instanceMatrix as any).array = null;
                        mesh.instanceMatrix = null as any;
                    }
                    // Dispose geometry and its attributes
                    if (mesh.geometry) {
                        Object.keys(mesh.geometry.attributes).forEach(attrName => {
                            const attr = mesh.geometry.attributes[attrName];
                            if (attr && 'array' in attr) {
                                (attr as any).array = null;
                            }
                        });
                        mesh.geometry.dispose();
                    }
                    // Dispose material(s)
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach(mat => mat.dispose());
                    } else {
                        mesh.material?.dispose();
                    }
                }
            });
        };
    }, []); // Only run on unmount

    const moleculeMatrix = useMemo(() => new THREE.Matrix4(), []);
    const localAtomMatrix = useMemo(() => new THREE.Matrix4(), []);
    const finalAtomMatrix = useMemo(() => new THREE.Matrix4(), []);
    const atomPosition = useMemo(() => new THREE.Vector3(), []);
    const atomQuaternion = useMemo(() => new THREE.Quaternion(), []);
    const atomScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);

    // Ref for custom ShaderMaterials (REMOVED)
    // const customMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
    const [customShaderMaterial, setCustomShaderMaterial] = useState<THREE.ShaderMaterial | null>(null);

    // Effect to create/update CUSTOM materials
    useEffect(() => {
        let newMaterial: THREE.ShaderMaterial | null = null;

        if (activeVisShader !== 'PBR_InstanceColor') {
            const selectedShaderInfo = customShaderRegistry[activeVisShader as keyof typeof customShaderRegistry];
            if (selectedShaderInfo) {
                const uniforms = THREE.UniformsUtils.clone(selectedShaderInfo.uniforms);
                newMaterial = new THREE.ShaderMaterial({
                    vertexShader: selectedShaderInfo.vertex,
                    fragmentShader: selectedShaderInfo.fragment,
                    uniforms: uniforms as { [uniform: string]: THREE.IUniform<any> },
                    lights: false,
                    depthTest: true,
                    depthWrite: true,
                    transparent: activeVisShader === 'SwirlEffect',
                });
            }
        }

        setCustomShaderMaterial(newMaterial);

        return () => {
            // Dispose of old material to prevent memory leak
            newMaterial?.dispose();
        };
    }, [activeVisShader]);

    // onBeforeCompile handler for the PBR case
    const handlePbrBeforeCompile = (shader: { vertexShader: string; fragmentShader: string; uniforms: { [key: string]: any } }) => {
        // Add attribute and varying declarations for both color and emissive
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `
            #include <common>
            attribute vec3 instanceColor;
            attribute vec3 instanceEmissive;
            varying vec3 vInstanceColor;
            varying vec3 vInstanceEmissive;
            `
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `
            #include <begin_vertex>
            vInstanceColor = instanceColor;
            vInstanceEmissive = instanceEmissive;
            `
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            `
            #include <common>
            varying vec3 vInstanceColor;
            varying vec3 vInstanceEmissive;
            `
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <color_fragment>',
            `
            #include <color_fragment> 
            diffuseColor.rgb = vInstanceColor;
            `
        );

        // Replace line 87: vec3 totalEmissiveRadiance = emissive;
        // with our instance-based emissive color
        shader.fragmentShader = shader.fragmentShader.replace(
            'vec3 totalEmissiveRadiance = emissive;',
            'vec3 totalEmissiveRadiance = vInstanceEmissive;'
        );
    };

    // --- Effect to set the instanceColor attribute (ACTIVE) ---
    useLayoutEffect(() => {
        const tempColor = new THREE.Color();
        const parseColor = (hex: string): [number, number, number] => {
            try {
                tempColor.set(hex);
                return [tempColor.r, tempColor.g, tempColor.b];
            } catch (e) {
                return [1, 1, 1];
            }
        };

        elementTypes.forEach(elementSymbol => {
            const mesh = meshRefs.current[elementSymbol];
            if (!mesh || !mesh.geometry) return;
            const elementProps = getElementProps(elementSymbol, elementProperties);
            const atomsInMolecule = atomsByElement[elementSymbol]?.length || 0;
            const totalAtomsOfElement = maxInstances * atomsInMolecule;

            // Dispose old instanceColor attribute to prevent memory leak
            const oldColorAttribute = mesh.geometry.getAttribute('instanceColor');
            if (oldColorAttribute) {
                (oldColorAttribute as THREE.BufferAttribute).array = null as any;
                mesh.geometry.deleteAttribute('instanceColor');
            }

            const oldEmissiveAttribute = mesh.geometry.getAttribute('instanceEmissive');
            if (oldEmissiveAttribute) {
                (oldEmissiveAttribute as THREE.BufferAttribute).array = null as any;
                mesh.geometry.deleteAttribute('instanceEmissive');
            }

            let colorArray;
            let emissiveArray;
            if (totalAtomsOfElement > 0) {
                colorArray = new Float32Array(totalAtomsOfElement * 3);
                emissiveArray = new Float32Array(totalAtomsOfElement * 3);

                // Check if this specific element is focused
                const isElementFocused = elementFocus[elementSymbol] || false;

                // Determine effective color based on hierarchy:
                // 1. Direct element color (if element is directly focused)
                // 2. Reaction color (if element is focused by a reaction)
                // 3. Species color (if element is focused by a species)
                // 4. Default element color
                let effectiveColor = elementProps.color; // Default fallback

                if (isElementFocused) {
                    // Check for direct element color first
                    if (elementColors[elementSymbol]) {
                        effectiveColor = elementColors[elementSymbol];
                    } else {
                        // Check for reaction color (element focused by reaction)
                        const focusedReactions = Object.keys(reactionFocus).filter(rid => reactionFocus[rid]);
                        for (const reactionId of focusedReactions) {
                            if (reactionColors[reactionId]) {
                                effectiveColor = reactionColors[reactionId];
                                break;
                            }
                        }

                        // If no reaction color, check for species color (element focused by species)
                        if (effectiveColor === elementProps.color) {
                            const focusedSpecies = Object.keys(speciesFocus).filter(sid => speciesFocus[sid]?.focusLevel === 'focus');
                            for (const speciesId of focusedSpecies) {
                                if (speciesColors[speciesId]) {
                                    effectiveColor = speciesColors[speciesId];
                                    break;
                                }
                            }
                        }
                    }
                }

                // Debug logging disabled - uncomment if needed
                // console.log(`[InstancedSpeciesMesh ${speciesName}] Element ${elementSymbol} - effectiveColor: ${effectiveColor}`);
                const [r, g, b] = parseColor(effectiveColor);

                // Calculate luminance and normalize to equalize bloom between elements
                const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
                const targetLuminance = 1.0; // Target 1.0 so emissiveIntensity directly controls bloom
                // Scale emissive to equalize brightness across different colored elements
                const scale = luminance > 0.01 ? (targetLuminance / luminance) : 1.0;

                for (let i = 0; i < totalAtomsOfElement; i++) {
                    colorArray[i * 3 + 0] = r;
                    colorArray[i * 3 + 1] = g;
                    colorArray[i * 3 + 2] = b;
                    emissiveArray[i * 3 + 0] = r * scale;
                    emissiveArray[i * 3 + 1] = g * scale;
                    emissiveArray[i * 3 + 2] = b * scale;
                }
            } else {
                colorArray = new Float32Array(3);
                colorArray.fill(1);
                emissiveArray = new Float32Array(3);
                emissiveArray.fill(1);
            }
            mesh.geometry.setAttribute(
                'instanceColor',
                new THREE.InstancedBufferAttribute(colorArray, 3)
            );
            mesh.geometry.setAttribute(
                'instanceEmissive',
                new THREE.InstancedBufferAttribute(emissiveArray, 3)
            );
        });
    }, [maxInstances, atomsByElement, elementTypes, elementProperties, speciesName, geometriesByElement, elementFocus, elementColors, reactionFocus, reactionColors, speciesFocus, speciesColors]);
    // --- End Attribute Effect ---

    useFrame(({ clock, camera }) => {
        // Get particle data from ThreeSceneManager (NOT from React props)
        const particles = threeSceneManager.getParticles(speciesName);
        const numInstances = particles.length;

        // Check if we need to grow the buffer (only grows, never shrinks)
        if (numInstances > peakCountRef.current) {
            peakCountRef.current = numInstances;
            if (numInstances > maxInstances * 0.9) { // 90% threshold
                const newMax = Math.ceil(numInstances * 1.5); // 50% headroom
                // console.log(`[InstancedSpeciesMesh ${speciesName}] Growing buffer: ${maxInstances} → ${newMax}`);
                setMaxInstances(newMax);
                return; // Skip this frame, will re-render with new buffer
            }
        }

        if (activeVisShader === 'SwirlEffect' && customShaderMaterial) {
            if (customShaderMaterial.uniforms.time) {
                customShaderMaterial.uniforms.time.value = clock.getElapsedTime();
            }
            if (customShaderMaterial.uniforms.u_cameraPosition) {
                camera.getWorldPosition(customShaderMaterial.uniforms.u_cameraPosition.value);
            }
            if (customShaderMaterial.uniforms.u_particleScale) {
                customShaderMaterial.uniforms.u_particleScale.value = particleScale;
            }
        }

        if (numInstances === 0) {
            // No particles yet - set mesh counts to 0
            Object.values(meshRefs.current).forEach(mesh => {
                if (mesh && mesh.count !== 0) {
                    mesh.count = 0;
                    if (mesh.instanceMatrix) mesh.instanceMatrix.needsUpdate = true;
                }
            });
            return;
        }

        let instanceCounters: { [elementSymbol: string]: number } = {};
        elementTypes.forEach(symbol => instanceCounters[symbol] = 0);

        for (let i = 0; i < numInstances; i++) {
            // Calculate molecule matrix from particle position and rotation
            const particle = particles[i];
            if (!particle) continue;

            const pos = new THREE.Vector3(...particle.position);
            const quat = new THREE.Quaternion(...particle.rotation);
            const scale = new THREE.Vector3(particleScale, particleScale, particleScale);
            moleculeMatrix.compose(pos, quat, scale);
            elementTypes.forEach(elementSymbol => {
                const mesh = meshRefs.current[elementSymbol];
                if (!mesh) return;

                const elementProps = getElementProps(elementSymbol, elementProperties);
                const baseRadius = typeof elementProps.radius === 'number' ? elementProps.radius : 0.5;
                const actualRadius = baseRadius * particleScale;

                atomsByElement[elementSymbol].forEach((localPosition) => {
                    const instanceIndex = instanceCounters[elementSymbol];
                    if (instanceIndex >= mesh.count) {
                        return;
                    }
                    atomPosition.copy(localPosition);
                    atomScale.set(actualRadius, actualRadius, actualRadius);
                    localAtomMatrix.compose(atomPosition, atomQuaternion, atomScale);
                    finalAtomMatrix.multiplyMatrices(moleculeMatrix, localAtomMatrix);
                    mesh.setMatrixAt(instanceIndex, finalAtomMatrix);
                    instanceCounters[elementSymbol]++;
                });
            });
        }

        // Update mesh counts and mark matrices as needing update
        Object.entries(meshRefs.current).forEach(([elementSymbol, mesh]) => {
            if (mesh && mesh.instanceMatrix) {
                // Set the actual count to the number of instances we wrote
                const actualCount = instanceCounters[elementSymbol] || 0;
                mesh.count = actualCount;
                mesh.instanceMatrix.needsUpdate = true;
            }
        });
    });

    // --- Return statement using MeshStandardMaterial with onBeforeCompile ---
    return (
        <>
            {elementTypes.map(elementSymbol => {
                const atomsInMolecule = atomsByElement[elementSymbol]?.length || 0;
                const totalAtomsOfElement = maxInstances * atomsInMolecule;
                const uniqueGeometry = geometriesByElement[elementSymbol];

                if (!uniqueGeometry || totalAtomsOfElement <= 0) return null;

                const isPbr = activeVisShader === 'PBR_InstanceColor';
                // Use the state variable for the custom material
                const currentCustomMaterial = customShaderMaterial;

                // Add detailed logging for PBR path check

                if (!isPbr && !currentCustomMaterial) {
                    return null;
                }

                const meshMaterial = isPbr ? undefined : (currentCustomMaterial || undefined);

                return (
                    <instancedMesh
                        key={`${speciesName}-${elementSymbol}`}
                        ref={el => {
                            if (el) {
                                meshRefs.current[elementSymbol] = el;
                                el.layers.set(1);  // Particles on layer 1
                            }
                        }}
                        args={[uniqueGeometry, undefined, totalAtomsOfElement]}
                        material={meshMaterial}
                        castShadow
                        receiveShadow
                    >
                        {isPbr ? (
                            <meshStandardMaterial
                                metalness={metalness}
                                roughness={roughness}
                                emissive="#ffffff"
                                emissiveIntensity={emissiveIntensity}
                                toneMapped={false}
                                onBeforeCompile={handlePbrBeforeCompile}
                            />
                        ) : (
                            null // Custom material passed via prop, render nothing as child
                        )}
                    </instancedMesh>
                );
            })}
        </>
    );
};

export default InstancedSpeciesMesh;
